import { useEffect } from "react";
import { useSelector } from "react-redux";
import { selectCurrentUserData, selectToken } from "../../store/auth";

const TAWK_LOADED_EVENT = "tawk-widget-loaded";

function safeTawkCall(label, fn) {
  try {
    fn();
  } catch (error) {
    console.warn(`Tawk.to: ${label}`, error);
  }
}

function ensureTawkReadyHook() {
  window.Tawk_API = window.Tawk_API || {};

  if (window.__tawkReadyHookInstalled) {
    return;
  }

  window.__tawkReadyHookInstalled = true;

  const previousOnLoad = window.Tawk_API.onLoad;
  window.Tawk_API.onLoad = function tawkOnLoadHandler() {
    window.__tawkWidgetReady = true;

    if (typeof previousOnLoad === "function") {
      safeTawkCall("previous onLoad handler", previousOnLoad);
    }

    window.dispatchEvent(new Event(TAWK_LOADED_EVENT));
  };

  if (window.Tawk_API.isLoaded) {
    window.__tawkWidgetReady = true;
    window.dispatchEvent(new Event(TAWK_LOADED_EVENT));
  }
}

function buildVisitorAttributes(userData) {
  const userName =
    userData?.name ||
    `${userData?.first_name || ""} ${userData?.last_name || ""}`.trim() ||
    userData?.email?.split("@")[0] ||
    "Portal User";

  return {
    name: userName,
    email: userData?.email || "",
    hash: String(userData?.staff_id || userData?.member_id || userData?.id || ""),
    userType: userData?.role?.name || (userData?.staff_id ? "Staff" : "Member"),
    companyName: userData?.company?.name || "",
    companyDomain: userData?.company?.domain_url || "",
    branchName: userData?.branch?.name || "",
  };
}

/**
 * Initializes tawk.to live chat widget with user identification.
 * Waits for Tawk onLoad before setAttributes to avoid i18next runtime errors.
 */
const TawkTo = () => {
  const userData = useSelector(selectCurrentUserData);
  const token = useSelector(selectToken);

  useEffect(() => {
    ensureTawkReadyHook();
  }, []);

  useEffect(() => {
    const hideWidget = () => {
      safeTawkCall("hideWidget", () => window.Tawk_API?.hideWidget?.());
    };

    const applyVisitor = () => {
      if (!token || !userData || !window.Tawk_API?.setAttributes) {
        return;
      }

      safeTawkCall("setAttributes", () => {
        window.Tawk_API.setAttributes(buildVisitorAttributes(userData), (error) => {
          if (error) {
            console.warn("Tawk.to: setAttributes callback error", error);
          }
        });
      });

      safeTawkCall("showWidget", () => window.Tawk_API?.showWidget?.());
    };

    const syncWidget = () => {
      if (!token || !userData) {
        hideWidget();
        return;
      }

      applyVisitor();
    };

    if (!window.__tawkWidgetReady) {
      window.addEventListener(TAWK_LOADED_EVENT, syncWidget);
      return () => window.removeEventListener(TAWK_LOADED_EVENT, syncWidget);
    }

    syncWidget();
  }, [userData, token]);

  return null;
};

export default TawkTo;
